/**
 * MCP 鍵発行ウィザード（UCX-04）の権限プリセット → access-policy policy-list 変換。
 *
 * UI（React コンポーネント）から独立させた純関数。preset の policy-list 内容は
 * ここが唯一の正本（テストで固定する）。
 *
 * 仕様: docs/spec/04-security.md §12.4/§12.6/§12.10
 *   - UI で発行した鍵は policy 皆無を「制限なし」にフォールバックせず、明示 allow-list を必ず作る
 *   - resource の engine/kind は正準 3 つ組（§12.4、core の access-policy-resources.ts と同値）。
 *     web-ui は core を import できないため、ここに正準値のミラー定数を置き全 UI が参照する
 *     （値の一致は core 側の貫通テスト uac-cx-policy-wiring-e2e.test.ts と本 preset テストで固定）
 *
 * プリセットは 3 択:
 *   - "full"（フルアクセス）: 13 tool + 4 source を全て明示 allow
 *   - "numbers-only"（数字のみ）: 数字系 9 tool のみ明示 allow。
 *     分析系 4 tool（context_recall / entity_context / memory_query / relation_explore）
 *     は allow policy を作らない（付与しない = デフォルト拒否のまま）。
 *     source は full と共通（数字系 tool も実データの参照に source アクセスを要する）
 *   - "no-meeting"（会議データ不可）: 13 tool allow + 会議系以外の source allow に加え、
 *     会議系 source（fieldy / meeting）へ明示 deny を作る（UAC-FIX-02。
 *     deny > allow の resolver 優先順位により、後から allow が足されても遮断が維持される）
 */

import type { UacAdminResourceEngine } from '../types/api'

// ─── UAC-FIX-01: 正準 3 つ組のミラー定数（core access-policy-resources.ts と同値） ───

/** 層1（MCP tool 単位 on/off）の正準 resource_engine。 */
export const TOOL_RESOURCE_ENGINE = 'mcp_tool' as const
/** 層1 の正準 resource_kind。 */
export const TOOL_RESOURCE_KIND = 'mcp_tool' as const
/** 層2（source 単位 on/off）の正準 resource_engine。 */
export const SOURCE_RESOURCE_ENGINE = 'source' as const
/** 層2 の正準 resource_kind。 */
export const SOURCE_RESOURCE_KIND = 'source' as const

export type AccessWizardPreset = 'full' | 'numbers-only' | 'no-meeting'

export interface PresetPolicyEntry {
  resourceEngine: UacAdminResourceEngine
  resourceKind: typeof TOOL_RESOURCE_KIND | typeof SOURCE_RESOURCE_KIND
  resourceId: string
  effect: 'allow' | 'deny'
}

/** MCP tool 13 種全量（AccessManagement.tsx の TOOL_RESOURCE_IDS と揃える）。 */
export const PRESET_ALL_TOOL_IDS = [
  'catalog_status',
  'object_metrics',
  'funnel_query',
  'harvest_overview',
  'sales_progress',
  'segmented_funnel',
  'confirmation_progress',
  'cost_breakdown',
  'context_recall',
  'entity_context',
  'memory_query',
  'relation_explore',
  'feedback_submit',
] as const

/** 「数字のみ」プリセットが許可する数字系 tool（集計・KPI 系のみ）。 */
export const PRESET_NUMERIC_TOOL_IDS = [
  'catalog_status',
  'object_metrics',
  'funnel_query',
  'harvest_overview',
  'sales_progress',
  'segmented_funnel',
  'confirmation_progress',
  'cost_breakdown',
  'feedback_submit',
] as const

/** 「数字のみ」プリセットが付与しない分析系 tool（参照用の明示リスト）。 */
export const PRESET_ANALYSIS_TOOL_IDS = [
  'context_recall',
  'entity_context',
  'memory_query',
  'relation_explore',
] as const

/** プリセットが対象にする source（GIFT pilot 実運用の 4 source）。 */
export const PRESET_SOURCE_IDS = ['salesforce', 'chatwork', 'google', 'fieldy'] as const

/**
 * 会議データ系 source。"fieldy" は会議（meeting）データ源の source id、"meeting" は
 * recall provenance 上の別名（mcp-server の source gate は meeting → fieldy に解決するが、
 * 将来 gate id が meeting のまま流れても遮断されるよう両方 deny する）。
 */
export const PRESET_MEETING_SOURCE_IDS = ['fieldy', 'meeting'] as const

function toolEntry(resourceId: string, effect: 'allow' | 'deny'): PresetPolicyEntry {
  return { resourceEngine: TOOL_RESOURCE_ENGINE, resourceKind: TOOL_RESOURCE_KIND, resourceId, effect }
}

function sourceEntry(resourceId: string, effect: 'allow' | 'deny'): PresetPolicyEntry {
  return { resourceEngine: SOURCE_RESOURCE_ENGINE, resourceKind: SOURCE_RESOURCE_KIND, resourceId, effect }
}

/**
 * preset → policy-list（resourceEngine/resourceKind/resourceId/effect の組）を生成する。
 * principalRef（userSlot）や actions は呼び出し側（createAccessPolicy 呼び出し）で付与する。
 * ここでは「何を allow / deny するか」だけを決定する。
 */
export function buildPresetPolicyEntries(preset: AccessWizardPreset): PresetPolicyEntry[] {
  if (preset === 'no-meeting') {
    return [
      ...PRESET_ALL_TOOL_IDS.map((id) => toolEntry(id, 'allow')),
      ...PRESET_SOURCE_IDS.filter(
        (id) => !(PRESET_MEETING_SOURCE_IDS as readonly string[]).includes(id),
      ).map((id) => sourceEntry(id, 'allow')),
      ...PRESET_MEETING_SOURCE_IDS.map((id) => sourceEntry(id, 'deny')),
    ]
  }
  const toolIds = preset === 'full' ? PRESET_ALL_TOOL_IDS : PRESET_NUMERIC_TOOL_IDS
  return [
    ...toolIds.map((id) => toolEntry(id, 'allow')),
    ...PRESET_SOURCE_IDS.map((id) => sourceEntry(id, 'allow')),
  ]
}

/** プリセットの表示用ラベル（ウィザード Step2 のカード見出し）。 */
export const PRESET_LABELS: Record<AccessWizardPreset, string> = {
  full: 'フルアクセス',
  'numbers-only': '数字のみ',
  'no-meeting': '会議データ不可',
}

/** プリセットの説明文（ウィザード Step2 のカード本文）。 */
export const PRESET_DESCRIPTIONS: Record<AccessWizardPreset, string> = {
  full: 'すべての tool（13種）と source（4種）を許可します。',
  'numbers-only': '集計・KPI 系とフィードバック受付の tool（9種）のみ許可します。個別の記録内容を検索・参照する分析系 tool は許可しません。',
  'no-meeting': 'すべての tool（13種）を許可しますが、会議データ（議事録）source へのアクセスを明示的に遮断します。',
}

// ─── 鍵一覧の権限プリセット推定（UCX-05） ───

/** 鍵一覧に表示する権限区分。プリセットに一致しない policy-list は 'custom'。 */
export type AccessPresetClassification = AccessWizardPreset | 'custom'

/** classifyAccessPreset が受け取る最小限の policy 形（AccessPolicy の部分型）。 */
export interface ClassifiableAccessPolicy {
  resourceEngine: UacAdminResourceEngine
  resourceId: string
  principalRef: string
  effect: 'allow' | 'deny'
}

function presetEntryKey(entry: { resourceEngine: UacAdminResourceEngine; resourceId: string }): string {
  return `${entry.resourceEngine}:${entry.resourceId}`
}

function keySetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const key of a) {
    if (!b.has(key)) return false
  }
  return true
}

function presetKeySets(preset: AccessWizardPreset): { allow: Set<string>; deny: Set<string> } {
  const entries = buildPresetPolicyEntries(preset)
  return {
    allow: new Set(entries.filter((e) => e.effect === 'allow').map(presetEntryKey)),
    deny: new Set(entries.filter((e) => e.effect === 'deny').map(presetEntryKey)),
  }
}

const PRESET_KEY_SETS: Record<AccessWizardPreset, { allow: Set<string>; deny: Set<string> }> = {
  full: presetKeySets('full'),
  'numbers-only': presetKeySets('numbers-only'),
  'no-meeting': presetKeySets('no-meeting'),
}

/**
 * principalRef（= userSlot）に付与された policy 集合から、権限プリセット
 * （'full' / 'numbers-only' / 'no-meeting'）に一致するか、それ以外（'custom'）かを推定する純関数。
 *
 * UCX-05: AccessManagement の鍵一覧「権限プリセット」列が使う。allow 集合と deny 集合の
 * **両方**が buildPresetPolicyEntries() の出力と完全一致する場合のみプリセット名を返し、
 * 部分一致・追加・欠落・policy 皆無はすべて 'custom' として扱う（曖昧な推定をしない）。
 */
export function classifyAccessPreset(
  policies: readonly ClassifiableAccessPolicy[],
  principalRef: string,
): AccessPresetClassification {
  const mine = policies.filter((p) => p.principalRef === principalRef)
  const allowedKeys = new Set(mine.filter((p) => p.effect === 'allow').map(presetEntryKey))
  const deniedKeys = new Set(mine.filter((p) => p.effect === 'deny').map(presetEntryKey))
  for (const preset of ['full', 'numbers-only', 'no-meeting'] as const) {
    const expected = PRESET_KEY_SETS[preset]
    if (keySetsEqual(allowedKeys, expected.allow) && keySetsEqual(deniedKeys, expected.deny)) {
      return preset
    }
  }
  return 'custom'
}

/** 4 区分の表示ラベル（鍵一覧の「権限プリセット」列が使う）。 */
export const ACCESS_PRESET_CLASSIFICATION_LABELS: Record<AccessPresetClassification, string> = {
  ...PRESET_LABELS,
  custom: 'カスタム',
}

// ─── claude mcp add コマンド生成 ───

/** VITE_MCP_SERVER_URL 未設定時に表示するプレースホルダ。 */
export const MCP_SERVER_URL_PLACEHOLDER = 'https://<接続先ホスト>/mcp'

/** 環境変数値から MCP サーバー URL を解決する。未設定 / 空文字はプレースホルダにフォールバックする。 */
export function resolveMcpServerUrl(envValue: string | undefined): string {
  const trimmed = envValue?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : MCP_SERVER_URL_PLACEHOLDER
}

function extractOrigin(mcpUrl: string): string {
  const trimmed = mcpUrl.trim().replace(/\/+$/, '')
  try {
    return new URL(trimmed).origin
  } catch {
    const match = /^(https?:\/\/[^/]+)/.exec(trimmed)
    return match?.[1] ?? trimmed
  }
}

export interface BuildClaudeMcpAddCommandParams {
  mcpUrl: string
  rawKey: string
  /** MCP サーバー名（`claude mcp add` の第一引数）。省略時は "ch-catalog"。 */
  serverName?: string
}

/**
 * `claude mcp add` コマンド文字列を生成する。
 * rawKey は呼び出し側が非永続に扱う責務を持つ（このコマンド文字列自体も含め、
 * component の state 以外の場所 — localStorage/log 等 — に置いてはならない）。
 */
export function buildClaudeMcpAddCommand(params: BuildClaudeMcpAddCommandParams): string {
  const serverName = params.serverName ?? 'ch-catalog'
  return `claude mcp add --transport http ${serverName} "${params.mcpUrl}" --header "Authorization: Bearer ${params.rawKey}"`
}

/** MCP server URL から plugin 配信 origin を抽出し、install.sh ワンライナーを生成する。 */
export function buildPluginInstallCommand(mcpUrl: string): string {
  const origin = extractOrigin(mcpUrl)
  return `curl -fsSL "${origin}/plugin/install.sh" | bash`
}
